import { fetchWorkspaceSkills, importSkillZip } from '../api/skills';

type DefaultSkillConfig = {
  skillName: string;
  zipUrl: string;
  zipFileName: string;
};

const DEFAULT_SKILLS_BY_PLATFORM: Record<string, DefaultSkillConfig[]> = {
  powerpoint: [
    {
      skillName: 'presidio-template',
      zipUrl: '/default-skills/presidio-template.zip',
      zipFileName: 'presidio-template.zip',
    },
  ],
};

export async function buildDefaultSkillPayload(
  workspaceId: string,
  platformName?: string | null,
): Promise<Array<{ skillId: string; isActive: boolean }> | undefined> {
  if (!platformName) return undefined;

  const configs = DEFAULT_SKILLS_BY_PLATFORM[platformName.toLowerCase()];
  if (!configs || configs.length === 0) return undefined;

  try {
    const existing = await fetchWorkspaceSkills(workspaceId, { limit: 100, offset: 0 });
    const skillMap = new Map(
      existing.skills.map((skill) => [skill.skillName?.toLowerCase(), skill.id]),
    );

    const missing = configs.filter((cfg) => !skillMap.has(cfg.skillName.toLowerCase()));

    for (const cfg of missing) {
      const response = await fetch(cfg.zipUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch skill zip (${response.status})`);
      }
      const blob = await response.blob();
      const zipFile = new File([blob], cfg.zipFileName, {
        type: blob.type || 'application/zip',
      });
      await importSkillZip(workspaceId, zipFile, cfg.skillName);
    }

    if (missing.length > 0) {
      const refreshed = await fetchWorkspaceSkills(workspaceId, { limit: 100, offset: 0 });
      for (const skill of refreshed.skills) {
        skillMap.set(skill.skillName?.toLowerCase(), skill.id);
      }
    }

    const payload = configs
      .map((cfg) => skillMap.get(cfg.skillName.toLowerCase()))
      .filter((id): id is string => Boolean(id))
      .map((skillId) => ({ skillId, isActive: true }));

    return payload.length > 0 ? payload : undefined;
  } catch (err) {
    console.warn('[default-agent-skills] Failed to build default skills:', err);
    return undefined;
  }
}
